import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';

import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Loadable from 'react-loadable';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import r from 'render-dom';
import styled from 'styled-components';

import Loading from 'components/ui/Loading';
import RedirectModal from 'components/public/Redirect';
import { smallMax } from 'constants/mediaQueries';
import { isMassview } from 'constants/settings';
import { useIsMobile } from 'hooks';
import { setJwt } from 'utils/jwt';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import ProviderWrapper from './ProviderWrapper';

const propTypes = {
    children: PropTypes.node,
    location: PropTypes.object,
};

const client = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

export const history = createBrowserHistory();

const Container = styled.div`
    display: flex;
`;

const Content = styled.div`
    box-sizing: border-box;
    flex: 1;
    height: calc(100vh - 100px);
    position: relative;

    @media(${smallMax}) {
        height: calc(100vh - 100px);
        margin-top: 50px;
    }
`;

const Nav = styled.div`
    bottom: 0;
    position: absolute;
    top: 0;
    z-index: 10;
`;

const AlertContainer = Loadable({
    loader: () => import('./components/shared/Alert'),
    loading: Loading,
    render: (loaded, props) => {
        const Component = loaded.AlertContainer;
        return r(Component, props);
    },
});
const Dialog = Loadable({
    loader: () => import('./components/shared/Dialog'),
    loading: Loading,
    render: (loaded) => {
        const { Dialog: dialogComponent, DialogContainer } = loaded;
        return r(DialogContainer, { dialogComponent });
    },
});
const App = Loadable({ loader: () => import('./components/App'), loading: Loading });
const ConfirmEmail = Loadable({
    loader: () => import('./components/public/ConfirmEmail'),
    loading: Loading,
});
const FiveOhThree = Loadable({
    loader: () => import('./components/public/FiveOhThree'),
    loading: Loading,
});
const FourOhFour = Loadable({
    loader: () => import('./components/public/FourOhFour'),
    loading: Loading,
});
const Login = Loadable({
    loader: () => import('./components/public/Login'),
    loading: Loading,
});
const Logout = Loadable({
    loader: () => import('./components/public/Logout'),
    loading: Loading,
});
const Navigation = Loadable({
    loader: () => import('./components/public/Navigation'),
    loading: Loading,
});
const Privacy = Loadable({
    loader: () => import('./components/public/Privacy'),
    loading: Loading,
});
const ResetPassword = Loadable({
    loader: () => import('./components/public/ResetPassword'),
    loading: Loading,
});
const Terms = Loadable({
    loader: () => import('./components/public/Terms'),
    loading: Loading,
});
const Toast = Loadable({
    loader: () => import('./components/public/Toast'),
    loading: Loading,
});
const Wizards = Loadable({
    loader: () => import('./components/public/Wizards'),
    loading: Loading,
});

export const publicRoutes = [
    { component: ConfirmEmail, path: '/confirm' },
    { component: FiveOhThree, path: '/maintenance' },
    { component: FourOhFour, path: '/404' },
    { component: Privacy, path: '/privacy' },
    { component: ResetPassword, path: '/reset' },
    { component: Wizards, path: '/signup' },
    { component: Wizards, path: ['/wizard/:type/:id', '/wizard'] },
    { component: Login, path: '/login' },
    { component: Logout, path: '/logout' },
    { component: Terms, path: '/terms' },
];

export default function Router() {
    const isMobile = useIsMobile();
    const { pathname } = history.location;
    const hideNavUrls = ['privacy', 'terms'];
    const hideNav = hideNavUrls.some((url) => pathname.includes(url)) && !isMobile;
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');

    useEffect(() => {
        if (isMassview) {
            const icons = document.querySelectorAll('link[rel="icon"]') || [];
            icons.forEach((icon) => {
                // eslint-disable-next-line no-param-reassign
                icon.href = icon.href.replace('snagshout-', '');
            });
        }

        if (token) {
            setJwt(token);
            searchParams.delete('token');
            const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';
            window.location.assign(`${window.location.origin}${window.location.pathname}${searchString}`);
        }
    }, []);

    return (
        r(QueryClientProvider, { client }, [
            r(ProviderWrapper, [
                r(BrowserRouter, { history }, [
                    r(Container, [
                        r(Helmet, [
                            r.title([
                                isMassview ? 'Massview' : 'Snagshout Partners'
                            ]),
                        ]),
                        r(Nav, { isRendered: !hideNav }, [
                            r(Route, { component: Navigation, path: '/' }),
                        ]),
                        r(Content, [
                            r(Switch, [
                                r(Route, { isRendered: isMassview, path: '*' }, [
                                    r(RedirectModal),
                                ]),
                                publicRoutes.map((props) => (
                                    r(Route, { ...props, key: props.path })
                                )),
                                r(Route, { path: '/' }, [
                                    r(App, { pathname }),
                                ]),
                            ]),
                        ]),
                        r(AlertContainer, { alertComponent: Toast }),
                        r(Dialog),
                    ]),
                ]),
            ]),
        ])
    );
}

Router.propTypes = propTypes;
